import { GroupsTable, GroupsTableType } from '../../components/GroupsTable/GroupsTable';
import { FC, Suspense, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { BpPage } from '../../components/BpPage/BpPage';
import { LazyLoader, Modal } from '@bp/ui-components';
import { GroupForm } from '../../components/GroupForm/GroupForm';
import { useAuthClaims } from '../../hooks/useAuthClaims';
import { OrganizationConfigContext } from '../../context/OrganizationConfigContext';
import { GroupSettingsForm } from 'components/GroupSettingsForm/GroupSettingsForm';
import dayjs from 'dayjs';
import { GroupFormOther } from '../../components/GroupForm/GroupFormOther';
import { graphql } from 'client/gql';
import { useMutation, useQuery } from 'urql';
import { GroupType } from 'client/gql/graphql';
import { useMatrixClient } from 'hooks/matrix/useMatrixClient';
import { usePermissionChecker } from 'hooks/usePermissionChecker';

const DeleteGroupMutationDocument = graphql(`
  mutation BpDeleteGroupFromGroups($id: ID!) {
    deleteBpGroup(uuid: $id) {
      entitiesDeleted
    }
  }
`);

const GroupsPagePageQueryDocument = graphql(`
  query GroupsPagePageData($groupWhere: GroupWhere!, $calendarEventWhere: CalendarEventWhere!, $profile: ID!) {
    groups(where: $groupWhere) {
      uuid
      __typename
      organization {
        uuid
        __typename
        displayName
      }
      name
      managedBy
      admins {
        uuid
        __typename
        displayNameShort
      }
      isCommunity
      schoolYear {
        uuid
        __typename
        name
      }
      editors {
        uuid
        __typename
      }
      viewers {
        uuid
        __typename
      }
      externalServices {
        __typename
        threema {
          __typename
          enabled
          lastSync
        }
        zoom {
          __typename
          enabled
          lastSync
        }
      }
      nextcloudConnection {
        __typename
        edges {
          __typename
          properties {
            enabled
            lastSync
          }
        }
      }
    }

    onlineEvents: calendarEvents(options: { sort: [{ start: ASC }], limit: 500 }, where: $calendarEventWhere) {
      uuid
      __typename

      virtualLocations {
        uuid
        __typename
        running
      }
      holderConnection {
        edges {
          node {
            __typename
            ... on Organization {
              uuid
            }
            ... on Group {
              uuid
            }
            ... on Profile {
              uuid
            }
          }
        }
      }
    }
    ...GroupsTableFields
    ...PermissionCheckerFields
  }
`);

export const GroupsPage: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pimAuthClaims, updateClaims } = useAuthClaims();
  const profileUuid = pimAuthClaims.getProfile().uuid;
  const organization = pimAuthClaims.getOrganization();

  const { currentSchoolYear } = useContext(OrganizationConfigContext);

  const [claimsTrigger, setClaimsTrigger] = useState<boolean>(false);

  const [formProfilesOpen, setFormProfilesOpen] = useState<boolean>(false);
  const [currentGroupUuid, setCurrentGroupUuid] = useState<string>('');

  const [, deleteGroup] = useMutation(DeleteGroupMutationDocument);

  const [{ data }] = useQuery({
    query: GroupsPagePageQueryDocument,
    variables: {
      profile: profileUuid,
      groupWhere: {
        organization: { uuid_IN: pimAuthClaims.getAllowedOrganizationUuids() },
        type: GroupType.Group,
        inCurrentSchoolyear: true,
        OR: [
          { editors_SOME: { uuid: pimAuthClaims.getProfile().uuid } },
          { viewers_SOME: { uuid: pimAuthClaims.getProfile().uuid } },
        ],
      },
      calendarEventWhere: {
        holderConnection: {
          Group: {
            node: {
              organization: { uuid_IN: pimAuthClaims.getAllowedOrganizationUuids() },
              type: GroupType.Group,
              inCurrentSchoolyear: true,
              OR: [
                { editors_SOME: { uuid: pimAuthClaims.getProfile().uuid } },
                { viewers_SOME: { uuid: pimAuthClaims.getProfile().uuid } },
              ],
            },
          },
        },
        virtualLocations_SOME: {
          running: true,
        },
      },
    },
  });

  const onlineEvents = data?.onlineEvents;
  const { rooms } = useMatrixClient();
  const perms = usePermissionChecker(data);
  const canCollaborate = perms?.canUpdateCollaborationGroups(organization);

  const tableData: GroupsTableType[] = useMemo(() => {
    return (
      data?.groups.map((group) => {
        const notificationRoom = rooms?.find((room) => room.name.includes(group.uuid));
        let newMessages = false;

        if (notificationRoom) {
          newMessages = notificationRoom.getRoomUnreadNotificationCount() > 0;
        }

        const nextCloud = group?.nextcloudConnection?.edges[0]?.properties;

        return {
          uuid: group.uuid,
          organizationUuid: group.organization.uuid,
          organizationName: group.organization.displayName ?? '',
          name: group.name,
          managedBy:
            group.managedBy !== 'bp'
              ? group.managedBy
              : (group.admins.map((admin) => admin.displayNameShort).join(', ') ?? group.managedBy),
          isCommunity: group.isCommunity,
          schoolyearName: group.schoolYear?.name ?? undefined,
          meetingInProgress:
            onlineEvents
              ?.find((v) => v.holderConnection.edges.find((e) => e.node.uuid)?.node.uuid === group.uuid)
              ?.virtualLocations.find((v) => v.running)?.running ?? false,
          admins: group.admins,
          newMessage: newMessages,
          editors: group.editors,
          viewers: group.viewers,
          externalServices: {
            threema: {
              enabled: group.externalServices?.threema?.enabled ?? false,
              lastSync: group.externalServices?.threema?.lastSync
                ? dayjs(group.externalServices?.threema.lastSync)
                : undefined,
            },
            zoom: {
              enabled: group.externalServices?.zoom?.enabled ?? false,
              lastSync: group.externalServices?.zoom?.lastSync
                ? dayjs(group.externalServices?.zoom.lastSync)
                : undefined,
            },
            nextcloud: {
              enabled: nextCloud?.enabled ?? false,
              lastSync: nextCloud?.lastSync ? dayjs(nextCloud?.lastSync) : undefined,
            },
          },
        };
      }) ?? []
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSchoolYear?.uuid, data?.groups, onlineEvents, rooms]);

  const [modalMode, setModalMode] = useState<{
    open: boolean;
    mode: 'create' | 'edit' | '';
    groupUuid?: string | null;
  }>({ open: false, mode: '', groupUuid: null });

  const handleAdd = () => {
    setModalMode({ open: true, mode: 'create' });
  };

  const handleEdit = (uuid: string) => {
    setModalMode({ open: true, mode: 'edit', groupUuid: uuid });
  };

  const handleDelete = async (uuid: string) => {
    await deleteGroup({ id: uuid });
  };

  const [settingsModalOpen, setSettingsModalOpen] = useState<{
    open: boolean;
    groupUuid: string | null;
  }>({ open: false, groupUuid: null });

  const handleSettings = (groupUuid: string) => {
    setSettingsModalOpen({ open: true, groupUuid: groupUuid });
  };

  const handleSettingsClose = () => {
    setSettingsModalOpen({ open: false, groupUuid: null });
  };

  const handleProfilesEdit = (uuid: string) => {
    setFormProfilesOpen(true);
    setCurrentGroupUuid(uuid);
  };

  return (
    <BpPage
      title={t('groups.myGroups', { year: currentSchoolYear?.name })}
      breakHeader
      isForbidden={false}
      organization={pimAuthClaims.getOrganization()}
    >
      <GroupsTable
        query={data}
        data={tableData}
        onNavigate={(uuid) => navigate('/groups/' + uuid)}
        onAdd={handleAdd}
        onEdit={handleEdit}
        onEditProfiles={handleProfilesEdit}
        onDelete={(uuid) => handleDelete(uuid)}
        onSettings={(uuid) => handleSettings(uuid)}
      />

      <Modal
        isOpen={modalMode.open}
        onRequestClose={() => setModalMode({ open: false, mode: '', groupUuid: null })}
        title={modalMode.mode === 'edit' ? t('groups.editGroup') : t('groups.addGroup')}
        hideFooter
      >
        <Suspense fallback={<LazyLoader embedded forceHeight='55vh' />}>
          <GroupForm
            canCollaborate={canCollaborate}
            groupUuid={modalMode.groupUuid ?? ''}
            onClose={async () => {
              setModalMode({ open: false, mode: '', groupUuid: null });
              await updateClaims(() => setClaimsTrigger(!claimsTrigger));
            }}
          />
        </Suspense>
      </Modal>

      <Modal
        title={t('courses.coursesSettings')}
        isOpen={settingsModalOpen.open}
        shouldCloseOnEsc={true}
        onRequestClose={handleSettingsClose}
      >
        <GroupSettingsForm onClose={handleSettingsClose} groupUuid={settingsModalOpen.groupUuid} />
      </Modal>

      <Modal
        isOpen={formProfilesOpen}
        onRequestClose={() => {
          setFormProfilesOpen(false);
          setCurrentGroupUuid('');
        }}
        title={t('groups.editGroup')}
        hideFooter
      >
        <Suspense fallback={<LazyLoader embedded forceHeight='30vh' />}>
          <GroupFormOther
            groupUuid={currentGroupUuid}
            onClose={async () => {
              await updateClaims(() => setClaimsTrigger(!claimsTrigger));
              setFormProfilesOpen(false);
              setCurrentGroupUuid('');
            }}
          />
        </Suspense>
      </Modal>
    </BpPage>
  );
};
